import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { API_BASE_URL } from '../config';

export const PlaylistDataContext = createContext({
  playlists: [],
  playlistsLoading: false,
  playlistsError: null,
  fetchPlaylists: async () => {},
  createPlaylist: async () => {},
  updatePlaylist: async () => {},
  deletePlaylist: async () => {},
  removePlaylistBackground: async () => false,
  addTracksToPlaylist: async () => null,
});

const PlaylistDataProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [playlists, setPlaylists] = useState([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlistsError, setPlaylistsError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showSuccessNotification = (message) => {
    addNotification(message, 'success');
  };

  const showErrorNotification = (message, requestBody) => {
    addNotification(`${message}. Request: ${JSON.stringify(requestBody)}`, 'error');
  };

  const removeNotification = (id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  // Fetch playlists
  const fetchPlaylists = async () => {
    if (authData?.accessToken) {
      setPlaylistsLoading(true);
      setPlaylistsError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/playlists/`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch playlists');
        }

        const data = await response.json();
        setPlaylists(data);
      } catch (error) {
        setPlaylistsError(error.message);
      } finally {
        setPlaylistsLoading(false);
      }
    }
  };

  // Create a new playlist
  const createPlaylist = async (playlistData) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/playlists/`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(playlistData),
        });

        if (!response.ok) {
          throw new Error('Failed to create playlist');
        }

        const newPlaylist = await response.json();
        setPlaylists((prev) => [...prev, newPlaylist]);
        showSuccessNotification(`Playlist created: ${newPlaylist.name}`);
      } catch (error) {
        showErrorNotification(error.message, JSON.stringify(playlistData));
      }
    }
  };

  // Update an existing playlist (PUT /playlists/{id}). Resolves with the
  // playlist the server answered so callers can show what was actually stored
  // (DJB-235: the server may keep the previous backgroundImage when the new
  // one is unknown to it), or with null when the update failed.
  const updatePlaylist = async (updatedPlaylist) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/playlists/${updatedPlaylist.id}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedPlaylist),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update playlist');
        }

        const updated = await response.json();
        setPlaylists((prev) =>
          prev.map((playlist) => (playlist.id === updated.id ? updated : playlist))
        );

        showSuccessNotification(`Playlist updated: ${updated.name}`);
        return updated;
      } catch (error) {
        showErrorNotification(error.message, updatedPlaylist);
        return null;
      }
    }
    return null;
  };

  /**
   * DELETE /playlists/{id}/backgroundImage (DJB-235): clears the background of
   * a playlist. A PUT with backgroundImage: null is not enough — the server
   * keeps the previous background on a null update — so removal has its own
   * endpoint. Resolves true when the background was removed (the playlist in
   * context loses its backgroundImage), false when the request failed.
   * @param {string} playlistId
   * @returns {Promise<boolean>}
   */
  const removePlaylistBackground = async (playlistId) => {
    if (!authData?.accessToken || !playlistId) return false;
    try {
      const response = await fetch(`${API_BASE_URL}/playlists/${playlistId}/backgroundImage`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove playlist background');
      }

      setPlaylists((prev) =>
        prev.map((playlist) =>
          playlist.id === playlistId ? { ...playlist, backgroundImage: null } : playlist
        )
      );
      showSuccessNotification('Playlist background removed');
      return true;
    } catch (error) {
      showErrorNotification(error.message, { playlistId });
      return false;
    }
  };

  /**
   * POST /playlists/{id}/tracks (DJB-272): appends tracks to a playlist
   * without touching the tracks already in it — unlike a PUT, which replaces
   * the whole trackset. The server answers `{ added, skipped }`; a track that
   * was already in the playlist comes back in `skipped` and is not duplicated.
   * The playlist in context gets the added tracks so the Playlist Editor
   * shows them without a refetch. Resolves with `{ added, skipped }`, or with
   * null when the request failed (400 unknown/deleted track or empty list,
   * 403 no rights, 404 unknown playlist). No global notification: callers
   * (DJB-266 match panel) report per playlist themselves.
   * @param {string} playlistId
   * @param {string[]} trackIds
   * @returns {Promise<{added: string[], skipped: string[]}|null>}
   */
  const addTracksToPlaylist = async (playlistId, trackIds) => {
    if (!authData?.accessToken || !playlistId || !trackIds?.length) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/playlists/${playlistId}/tracks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ trackIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add tracks to playlist (${response.status})`);
      }

      const body = await response.json();
      const result = { added: body?.added || [], skipped: body?.skipped || [] };
      if (result.added.length > 0) {
        setPlaylists((prev) =>
          prev.map((playlist) => {
            if (playlist.id !== playlistId) return playlist;
            const tracks = playlist.tracks || [];
            return { ...playlist, tracks: [...tracks, ...result.added.filter((id) => !tracks.includes(id))] };
          })
        );
      }
      return result;
    } catch (error) {
      console.error('Failed to add tracks to playlist:', error);
      return null;
    }
  };

  // Delete a playlist
  const deletePlaylist = async (playlistId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/playlists/${playlistId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete playlist');
        }

        setPlaylists((prev) => prev.filter((playlist) => playlist.id !== playlistId));
        showSuccessNotification('Playlist deleted successfully');
      } catch (error) {
        showErrorNotification(error.message, { playlistId });
      }
    }
  };

  useEffect(() => {
    fetchPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  return (
    <PlaylistDataContext.Provider
      value={{
        playlists,
        playlistsLoading,
        playlistsError,
        fetchPlaylists,
        createPlaylist,
        updatePlaylist,
        deletePlaylist,
        removePlaylistBackground,
        addTracksToPlaylist,
      }}
    >
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </PlaylistDataContext.Provider>
  );
};

export default PlaylistDataProvider;
